import React, { useEffect } from "react";
import {
  Card,
  CardBody,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import classnames from "classnames";
import { Link, useSearchParams } from "react-router-dom";
import OverviewTab from "./OverviewTab";
import ContactsTab from "./ContactsTab";
import { DateTime } from "components/common/Date";
import { useClientOverview } from "./ClientOverviewContext";
import Avatar from "components/common/Avatar";
import { getClientAvatarUrl } from "features/Clients/utils/clientAvatarUrl";
import { getClientDisplayName } from "features/Clients/utils/clientDisplayName";
import {
  clientOverviewTabIdFromSearchParam,
  isValidClientOverviewTabParam,
  searchParamForClientOverviewTabId,
} from "./overviewTabSearchParams";

const Section = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { client } = useClientOverview();

  const tabQuery = searchParams.get("tab");
  const activeTab = clientOverviewTabIdFromSearchParam(tabQuery);

  useEffect(() => {
    if (tabQuery && !isValidClientOverviewTabParam(tabQuery)) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("tab");
          return next;
        },
        { replace: true },
      );
    }
  }, [tabQuery, setSearchParams]);

  const setOverviewTabInUrl = (tabId: string) => {
    const tabParam = searchParamForClientOverviewTabId(tabId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tabParam) {
          next.set("tab", tabParam);
        } else {
          next.delete("tab");
        }
        return next;
      },
      { replace: true },
    );
  };

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card className="mt-n4 mx-n4">
            <div className="bg-light-subtle">
              <CardBody className="pb-0 px-4">
                <Row className="mb-3">
                  <div className="col-md">
                    <Row className="align-items-center g-3">
                      <div className="col-auto">
                        <div className="avatar-lg rounded-circle overflow-hidden">
                          <Avatar
                            name={getClientDisplayName(client)}
                            image={getClientAvatarUrl(client?.image)}
                            className="fs-20 w-100 h-100"
                          />
                        </div>
                      </div>
                      <div className="col-md">
                        <h4 className="fw-bold mb-1">
                          {getClientDisplayName(client)}
                        </h4>
                        <div className="hstack gap-3 flex-wrap">
                          {client?.company ? (
                            <div>
                              Company:{" "}
                              <span className="fw-medium">{client.company}</span>
                            </div>
                          ) : null}
                          {client?.created_at ? (
                            <>
                              <div className="vr" />
                              <div>
                                Created:{" "}
                                <span className="fw-medium">
                                  <DateTime date={client.created_at} />
                                </span>
                              </div>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </Row>
                  </div>
                  <div className="col-md-auto">
                    <div className="hstack gap-2 flex-nowrap">
                      {client?.id ? (
                        <Link
                          to={`/clients/edit/${client.id}`}
                          className="btn btn-primary btn-sm"
                        >
                          <i className="ri-pencil-line align-bottom me-1" />
                          Edit
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </Row>

                <Nav
                  className="nav-tabs-custom border-bottom-0"
                  role="tablist"
                >
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "1" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setOverviewTabInUrl("1");
                      }}
                      href="#"
                    >
                      Overview
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames(
                        { active: activeTab === "2" },
                        "text-body fw-semibold",
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        setOverviewTabInUrl("2");
                      }}
                      href="#"
                    >
                      Contacts
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardBody>
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <TabContent activeTab={activeTab} className="text-muted">
            <TabPane tabId="1">
              <OverviewTab />
            </TabPane>
            <TabPane tabId="2">
              <ContactsTab />
            </TabPane>
          </TabContent>
        </Col>
      </Row>
    </React.Fragment>
  );
};

export default Section;
